import classNames from "classnames";
import { inject, observer } from "mobx-react";
import React from "react";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import {
  PasswordValidation,
  validatePasswordPolicy
} from "@fidget/common/utils/validatePasswordPolicy";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { IUiStore } from "../../../models/ui/UiStore";
import { EditMeLabel } from "../../editMeLabel/editMeLabel";
import { PasswordRules } from "../../passwordRules";
import { DiscardApplyButtons } from "../discardApplyButtons";
import styles from "./passwordSettings.css";

export interface Props {
  currentUserStore?: ICurrentUserStore;
  uiStore?: IUiStore;
}

export interface State {
  editPassword: boolean;
  currentPassword: string;
  newPassword: string;
  newPasswordRepeated: string;
  passwordValidation: PasswordValidation;
  passwordsDoNotMatch: boolean;
}

@inject("currentUserStore", "uiStore")
@observer
export class PasswordSettings extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editPassword: false,
      currentPassword: "",
      newPassword: "",
      newPasswordRepeated: "",
      passwordValidation: {
        longEnough: false,
        hasOneNumber: false,
        hasUppercase: false
      },
      passwordsDoNotMatch: false
    };
  }

  componentDidMount() {
    this.props.currentUserStore!.clearErrors();
  }

  enterPasswordEditing = () => {
    this.setState({ editPassword: true });
  };

  closePasswordEditing = () => {
    this.setState({
      editPassword: false,
      currentPassword: "",
      newPassword: "",
      newPasswordRepeated: "",
      passwordValidation: {
        longEnough: false,
        hasOneNumber: false,
        hasUppercase: false
      },
      passwordsDoNotMatch: false
    });
    this.props.currentUserStore!.clearErrors();
  };

  handleNewPassword = (value: string) => {
    this.setState({
      newPassword: value,
      passwordsDoNotMatch: false,
      passwordValidation: validatePasswordPolicy(value)
    });
  };

  handleNewPasswordRepeated = (value: string) => {
    this.setState({ newPasswordRepeated: value, passwordsDoNotMatch: false });
  };

  isButtonDisabled = () =>
    Object.values(this.state.passwordValidation).includes(false) || !this.state.newPasswordRepeated;

  onSave = () => {
    const { currentUserStore, uiStore } = this.props;
    const { currentPassword, newPassword, newPasswordRepeated } = this.state;

    currentUserStore!.clearErrors();

    if (newPassword === newPasswordRepeated) {
      currentUserStore!
        .changePasswordWithCurrentPassword(currentPassword, newPassword)
        .then(() => {
          this.closePasswordEditing();
          uiStore!.addSuccessToast(intl.formatMessage("settings.profile.password.success"));
        })
        .catch((reason) => {
          if (!currentUserStore!.loginError) {
            // signal unidentified error eg. other than wrong password
            uiStore!.addErrorToast(intl.formatMessage("error.something"));
          }
        });
    } else {
      this.setState({ passwordsDoNotMatch: true });
    }
  };

  renderEdit = () => {
    const { currentUserStore } = this.props;
    const wrongCurrentPassword = currentUserStore!.loginError;

    return (
      <form onSubmit={(ev) => ev.preventDefault()}>
        <div className={styles.title}>
          <L10n id={"settings.profile.password.changeBelow"} />
        </div>
        <InputGroup>
          <EditInput
            className={classNames(styles["password-input"], {
              [styles.error]: wrongCurrentPassword
            })}
            type={"password"}
            value={this.state.currentPassword}
            placeholder={intl.formatMessage("settings.profile.password.typeInCurrent")}
            onChange={(ev) =>
              this.setState(
                {
                  currentPassword: (ev.target as HTMLInputElement).value
                },
                currentUserStore!.clearErrors
              )
            }
            autoFocus
            autoComplete={"current-password"}
          />
        </InputGroup>
        <div className={styles["error-details"]}>
          {wrongCurrentPassword && (
            <L10nHtml id={"settings.profile.password.error.wrongCurrentPassword"} />
          )}
        </div>
        <PasswordRules
          passwordValidation={this.state.passwordValidation}
          okColor={"#00AB8E"}
          inline
        />
        <InputGroup>
          <EditInput
            className={classNames(styles["password-input"], {
              [styles.error]: this.state.passwordsDoNotMatch
            })}
            type={"password"}
            value={this.state.newPassword}
            placeholder={intl.formatMessage("login.password.placeholder.newPassword")}
            onChange={(ev) => this.handleNewPassword((ev.target as HTMLInputElement).value)}
            autoComplete={"new-password"}
          />
        </InputGroup>
        <InputGroup>
          <EditInput
            className={classNames(styles["password-input"], {
              [styles.error]: this.state.passwordsDoNotMatch
            })}
            type={"password"}
            value={this.state.newPasswordRepeated}
            placeholder={intl.formatMessage("login.password.placeholder.repeatNewPassword")}
            onChange={(ev) => this.handleNewPasswordRepeated((ev.target as HTMLInputElement).value)}
            autoComplete={"new-password"}
          />
        </InputGroup>
        <div className={classNames(styles["error-details"], styles.longError)}>
          {this.state.passwordsDoNotMatch && (
            <L10nHtml id={"settings.profile.password.error.description"} />
          )}
        </div>
        <DiscardApplyButtons
          onApply={this.onSave}
          onDiscard={this.closePasswordEditing}
          applyDisabled={this.isButtonDisabled()}
          alignToLeft
        />
      </form>
    );
  };

  renderDisplay = () => {
    return (
      <EditMeLabel
        value={intl.formatMessage("settings.profile.password.change")}
        onClick={this.enterPasswordEditing}
        className={styles["edit-label"]}
      />
    );
  };

  render() {
    return (
      <div className={styles.container}>
        {this.state.editPassword ? this.renderEdit() : this.renderDisplay()}
      </div>
    );
  }
}
